/**
 * WaveIndicator Component
 *
 * For Wave Survival mode - displays current wave progress, enemy counts,
 * and time until next wave.
 */

import React, { useEffect, useState } from 'react';
import styles from './WaveIndicator.module.css';

export interface WaveIndicatorProps {
  /** Current wave number (1-indexed) */
  currentWave: number;
  /** Total number of waves */
  totalWaves: number;
  /** Enemies remaining in current wave */
  enemiesRemaining: number;
  /** Total enemies in current wave */
  totalEnemies: number;
  /** Seconds until next wave spawns (undefined if in combat) */
  timeUntilNextWave?: number;
  /** Whether the wave is currently spawning enemies */
  isSpawning?: boolean;
  /** Optional callback when wave timer expires */
  onWaveTimerExpired?: () => void;
}

export const WaveIndicator: React.FC<WaveIndicatorProps> = ({
  currentWave,
  totalWaves,
  enemiesRemaining,
  totalEnemies,
  timeUntilNextWave,
  isSpawning = false,
  onWaveTimerExpired,
}) => {
  const [displayTime, setDisplayTime] = useState(timeUntilNextWave ?? 0);

  // Update display time when prop changes
  useEffect(() => {
    setDisplayTime(timeUntilNextWave ?? 0);
  }, [timeUntilNextWave]);

  // Countdown timer
  useEffect(() => {
    if (displayTime <= 0 || timeUntilNextWave === undefined) return;

    const interval = setInterval(() => {
      setDisplayTime(prev => {
        const newTime = Math.max(0, prev - 1);
        if (newTime === 0 && onWaveTimerExpired) {
          onWaveTimerExpired();
        }
        return newTime;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [displayTime, timeUntilNextWave, onWaveTimerExpired]);

  const waveProgress = ((currentWave - 1) / totalWaves) * 100 +
    ((totalEnemies - enemiesRemaining) / totalEnemies) * (100 / totalWaves);

  const enemyProgress = totalEnemies > 0
    ? ((totalEnemies - enemiesRemaining) / totalEnemies) * 100
    : 0;

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}:${secs.toString().padStart(2, '0')}` : `${secs}s`;
  };

  const isLastWave = currentWave === totalWaves;
  const isWaveComplete = enemiesRemaining === 0;

  return (
    <div className={styles.waveIndicator}>
      {/* Wave Header */}
      <div className={styles.waveHeader}>
        <div className={styles.waveLabel}>
          <span className={styles.waveIcon}>waves</span>
          <span className={styles.waveText}>WAVE</span>
        </div>
        <div className={styles.waveNumber}>
          <span className={styles.current}>{currentWave}</span>
          <span className={styles.separator}>/</span>
          <span className={styles.total}>{totalWaves}</span>
        </div>
      </div>

      {/* Overall Progress Bar */}
      <div className={styles.progressBarContainer}>
        <div className={styles.progressBar}>
          <div
            className={styles.progressFill}
            style={{ width: `${waveProgress}%` }}
          />
          {/* Wave markers */}
          {Array.from({ length: totalWaves - 1 }, (_, i) => (
            <div
              key={i}
              className={styles.waveMarker}
              style={{ left: `${((i + 1) / totalWaves) * 100}%` }}
            />
          ))}
        </div>
      </div>

      {/* Current Wave Status */}
      <div className={styles.waveStatus}>
        {isSpawning ? (
          <div className={styles.spawningIndicator}>
            <span className={styles.spawningIcon}>alert</span>
            <span className={styles.spawningText}>SPAWNING...</span>
          </div>
        ) : isWaveComplete && !isLastWave ? (
          <div className={styles.nextWaveTimer}>
            <span className={styles.timerLabel}>Next wave in:</span>
            <span className={styles.timerValue}>{formatTime(displayTime)}</span>
          </div>
        ) : isLastWave && isWaveComplete ? (
          <div className={styles.finalWaveComplete}>
            <span className={styles.checkIcon}>check-circle</span>
            <span>FINAL WAVE CLEARED</span>
          </div>
        ) : (
          <div className={styles.enemyCount}>
            <span className={styles.enemyIcon}>skull</span>
            <span className={styles.enemyLabel}>Enemies:</span>
            <span className={styles.enemyValue}>
              {enemiesRemaining} / {totalEnemies}
            </span>
          </div>
        )}
      </div>

      {/* Enemy Progress Bar (within current wave) */}
      {!isWaveComplete && (
        <div className={styles.enemyProgressContainer}>
          <div className={styles.enemyProgressBar}>
            <div
              className={styles.enemyProgressFill}
              style={{ width: `${enemyProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Wave Complete Flash */}
      {isWaveComplete && !isLastWave && (
        <div className={styles.waveCompleteFlash}>
          <span>WAVE {currentWave} COMPLETE</span>
        </div>
      )}
    </div>
  );
};
